/**
 * The council's trade policy. What the AI crowns do with their merchants.
 *
 * A system the player can play and the AI cannot is a menu, not a mechanic — so
 * this is not optional colour, it is half the feature. Every crown on the board
 * posts, retasks and recalls through exactly the same `postMerchant` and
 * `recallMerchant` the player's commands go through. There is no second door and
 * no hidden bonus.
 *
 * **It draws no random numbers, deliberately.** Every other AI decision in this
 * layer rolls off `w.rngCampaign`, and adding a draw here would shift that
 * stream for every existing system — the note in `COORDINATION.md` about a
 * single unit in a roster moving `campaign-depth` into its unlucky third is
 * exactly this hazard. Crowns are staggered by `(turn + nation) % TRADE_COUNCIL_PERIOD`
 * instead: two dozen merchants do not all reshuffle in the same month, nothing
 * moves in lockstep, and the campaign RNG stream is untouched by this entire
 * feature.
 *
 * The policy in full, because a strategic AI the player cannot predict is
 * indistinguishable from one that is cheating:
 *
 *   1. **Come home** from any market where our share has fallen to nothing. A
 *      merchant standing in somebody else's node earns nothing and is the only
 *      wasted asset in the system.
 *   2. **Score every reachable market**, both ways round — what a collector
 *      would earn there, and what a steerer would push into a market we already
 *      dominate — and take the best of them if it beats doing nothing.
 *   3. **Retask before posting.** A man already standing there is free to
 *      re-order; a new posting costs months on the road.
 */

import type { CampaignState } from './types.ts';
import {
  MerchantMission,
  STEER_PULL,
  retentionOf,
  steerWeightOf,
  type Merchant,
} from './tradetypes.ts';
import { controlIn, tradeNodeOf } from './trade.ts';
import { atWar } from './state.ts';
import { freeMerchants, merchantAt, merchantsOf, postMerchant, recallMerchant } from './merchants.ts';

/**
 * One crown in three sits down about trade each month.
 *
 * Not a throttle for cost — the whole sitting is a few hundred multiplications.
 * It is there so the board does not move as one animal: staggered, a rival's
 * merchant arrives in a market a season after yours did and has to decide about
 * a node that has already changed, which is what makes the competition read as
 * a competition.
 */
export const TRADE_COUNCIL_PERIOD = 3;

/** Below this share of a node, a merchant standing there is wasted. */
const RECALL_FLOOR = 0.06;

/**
 * Roughly how much control one merchant buys in a market.
 *
 * A guess, and it is written down as a guess. The exact answer needs the node's
 * total trade power, which is not stored — storing it would be storing a
 * denominator so the AI can divide by it, which is a worse trade than being
 * approximately right. Nine points of merchant against a typical node total of
 * forty to sixty is about a sixth, and the decisions this feeds are ranked
 * against each other rather than against a threshold, so a common bias in the
 * estimate cancels.
 */
const MERCHANT_CONTROL_GUESS = 0.18;

/** Below this much expected gold a month, leave the merchant where he is. */
const WORTH_MOVING = 0.6;

/**
 * What a gold piece taken off an enemy is worth to us, next to one we earn.
 *
 * The term that makes steering a verb the council actually reaches for, and it is
 * a correction to the scoring rather than a thumb on the scale. Draining a market
 * already robs whoever is collecting in it — that is what `retainFloorWith` does
 * in the flow, and it happens whether anybody scores it or not. The council was
 * simply blind to it: it counted what a steerer would earn downstream and never
 * counted what the same act took off the crown it is at war with.
 *
 * Measured, that blindness was decisive. A steer scored one to three gold a month
 * against a collector's fourteen, so no crown on the board ever chose one in a
 * hundred and fifty months — not because collecting was the better play, but
 * because the only half of steering being valued was the half that pays late.
 *
 * Half rather than the whole, because a coin denied is worth less than a coin
 * banked: it does not build anything, and the enemy may take the market back.
 * That it applies only in war is the point — this is a blockade, and a blockade
 * on a crown you are at peace with is just a worse way to make money.
 */
const WAR_DENIAL = 0.5;

export interface Option {
  node: number;
  mission: number;
  toward: number;
  gain: number;
}

/**
 * Every posting this crown could make, best first, in expected gold a month.
 *
 * The council's whole judgement, exported: the AI ranks with it, and a panel that
 * wants to tell the player where their next factor would earn most can rank with
 * the same numbers rather than inventing a second opinion.
 */
export function tradeOptions(c: CampaignState, n: number): Option[] {
  const options: Option[] = [];
  if (!c.trade || c.trade.nodes.length === 0) return options;
  for (const k of reachableNodes(c, n)) {
    const collect = collectGain(c, n, k);
    if (collect > 0) {
      options.push({ node: k, mission: MerchantMission.Collect, toward: -1, gain: collect });
    }
    for (const to of c.trade.nodes[k].outgoing) {
      const steer = steerGain(c, n, k, to);
      if (steer > 0) options.push({ node: k, mission: MerchantMission.Steer, toward: to, gain: steer });
    }
  }
  // Ties break toward the lower node, then the earlier mission: arbitrary, and
  // the same every time, which is the part that matters.
  options.sort((a, b) => b.gain - a.gain || a.node - b.node || a.mission - b.mission);
  return options;
}

export function tradeCouncil(c: CampaignState): void {
  if (c.over) return;
  const t = c.trade;
  if (!t || t.nodes.length === 0) return;

  for (let n = 0; n < c.nations.length; n++) {
    if (n === c.playerNation) continue;
    if (!c.nations[n].alive) continue;
    if ((c.turn + n) % TRADE_COUNCIL_PERIOD !== 0) continue;
    nationTrade(c, n);
  }
}

/**
 * One crown's trade policy, off the stagger.
 *
 * `tradeCouncil` is the monthly entry point and skips two crowns in three so the
 * board does not move as one animal. This is the same sitting without that gate,
 * for a caller that has already decided this crown should think — the tests, and
 * anything that ever wants to hand a newly-freed AI its bearings immediately.
 */
export function tradeCouncilFor(c: CampaignState, n: number): void {
  if (c.over) return;
  const t = c.trade;
  if (!t || t.nodes.length === 0) return;
  if (n < 0 || n >= c.nations.length || !c.nations[n].alive) return;
  nationTrade(c, n);
}

function nationTrade(c: CampaignState, n: number): void {
  // 1. Come home from anywhere we have lost.
  for (const m of merchantsOf(c, n)) {
    if (m.travel > 0) continue;
    if (controlIn(c, m.node, n) < RECALL_FLOOR) recallMerchant(c, n, m.node);
  }

  const mine = merchantsOf(c, n);
  const posted = new Map<number, Merchant>();
  for (const m of mine) posted.set(m.node, m);

  // 2. Score everything, both ways round.
  const options = tradeOptions(c, n);
  if (options.length === 0) return;

  // 3. Retask first — a man already there is free to re-order. Only then spend a
  // merchant we have spare on the best thing left.
  for (const opt of options) {
    const sitting = posted.get(opt.node);
    if (!sitting) continue;
    if (sitting.mission === opt.mission && sitting.toward === opt.toward) continue;
    const now = currentGain(c, n, sitting);
    if (opt.gain <= now + WORTH_MOVING) continue;
    postMerchant(c, n, opt.node, opt.mission, opt.toward);
    posted.set(opt.node, sitting);
  }

  let spare = freeMerchants(c, n);
  for (const opt of options) {
    if (spare <= 0) break;
    if (posted.has(opt.node)) continue;
    if (opt.gain < WORTH_MOVING) break;
    const m = postMerchant(c, n, opt.node, opt.mission, opt.toward);
    if (!m) continue;
    posted.set(opt.node, m);
    spare--;
  }
}

/** What the merchant already standing there is doing for us. */
function currentGain(c: CampaignState, n: number, m: Merchant): number {
  return m.mission === MerchantMission.Collect
    ? collectGain(c, n, m.node)
    : steerGain(c, n, m.node, m.toward);
}

/**
 * What a collector would add here, in gold a month.
 *
 * Two effects, and both of them are real: the merchant buys a little more of the
 * node, and — everywhere but a terminal — his presence keeps more of the node's
 * value from running downstream in the first place. The second term is usually
 * the larger one, which is why the AI puts its first merchant in its own richest
 * inland market rather than on the coast.
 */
function collectGain(c: CampaignState, n: number, k: number): number {
  const st = c.trade.states[k];
  if (!st || st.value <= 0) return 0;
  const ctrl = st.control[n] ?? 0;
  const terminal = c.trade.nodes[k].outgoing.length === 0;
  const dControl = MERCHANT_CONTROL_GUESS * (1 - ctrl);
  const after = Math.min(1, ctrl + dControl);

  // Retention through `retentionOf`, not a second copy of the rule — the council
  // reasoning from its own arithmetic is how an opponent starts looking like it
  // is cheating. Our own share is taken out of the node's collect share before
  // our post-merchant share is put back, so a man already standing here is not
  // counted twice. `steerShare` stays in: a rival steering this market is
  // draining it, and that is precisely when a collector is worth sending.
  const sitting = merchantAt(c, n, k);
  const mineNow = sitting && sitting.travel <= 0 && sitting.mission === MerchantMission.Collect ? ctrl : 0;
  const others = Math.max(0, st.collectShare - mineNow);
  const retainAfter = terminal ? 1 : retentionOf(others + after, st.steerShare);
  return st.value * retainAfter * after - st.income[n];
}

/**
 * What a steerer would add, in gold a month — earned *downstream*, not here.
 *
 * Only worth anything when we hold the far end harder than this one, which is
 * the whole shape of the decision: steering is how a crown that owns a port pays
 * for it with somebody else's hinterland. The pull is scaled by our control
 * here, so a market we barely hold barely moves.
 */
function steerGain(c: CampaignState, n: number, k: number, to: number): number {
  const st = c.trade.states[k];
  const dst = c.trade.states[to];
  const node = c.trade.nodes[k];
  if (!st || !dst || st.value <= 0) return 0;

  const here = st.control[n] ?? 0;
  const there = dst.control[n] ?? 0;
  // No gate on `there > here`. There used to be one, on the argument that
  // steering is only worth anything when you hold the far end harder than this
  // one — which is true of steering *for profit* and false of steering to starve
  // an enemy, and the second is most of why a crown at war would do it. The three
  // terms below already price both cases honestly: a steer that helps nobody
  // comes out negative on the cost term alone and is dropped by the caller.
  const i = node.outgoing.indexOf(to);
  if (i < 0) return 0;
  let base = 0;
  for (const w of node.outWeight) base += w;
  if (base <= 0) return 0;

  const sitting = merchantAt(c, n, k);
  const working = sitting !== undefined && sitting.travel <= 0;
  const ours = steerWeightOf(here);
  const mineSteer = working && sitting.mission === MerchantMission.Steer ? ours : 0;
  const mineCollect = working && sitting.mission === MerchantMission.Collect ? here : 0;

  // Both halves of what a steerer does, because the draining half is the larger
  // one and an AI blind to it never steers. See `retainFloorWith`.
  const collectAfter = Math.max(0, st.collectShare - mineCollect);
  const steerAfter = Math.min(1, Math.max(0, st.steerShare - mineSteer) + ours);
  const retainAfter = node.outgoing.length === 0 ? 1 : retentionOf(collectAfter, steerAfter);

  const leavingNow = st.value * (1 - st.retained);
  const leavingAfter = st.value * (1 - retainAfter);

  const nowShare = node.outWeight[i] / base;
  const pull = STEER_PULL * ours;
  const newShare = Math.min(1, (node.outWeight[i] * (1 + pull)) / (base + node.outWeight[i] * pull));

  // What arrives downstream is worth whatever we can take of it there.
  const arrivesNow = leavingNow * nowShare;
  const arrivesAfter = leavingAfter * newShare;
  const downstream = (arrivesAfter - arrivesNow) * dst.retained * there;

  // And the price, computed rather than guessed: draining this node costs us our
  // own cut of what no longer stays in it. Without this term a crown will steer
  // away value it was already being paid for.
  const hereNow = st.value * st.retained * here;
  const hereAfter = st.value * retainAfter * here;

  // The other half of the act: everything we drag out of this market comes off
  // whoever was collecting it. Against a crown we are at war with that is not a
  // side effect, it is the reason to do it — a blockade of the road rather than
  // of the harbour. See `WAR_DENIAL`.
  let enemyShare = 0;
  for (let e = 0; e < c.nations.length; e++) {
    if (e === n || !c.nations[e].alive) continue;
    if (atWar(c, n, e)) enemyShare += st.control[e] ?? 0;
  }
  const denied = st.value * Math.max(0, st.retained - retainAfter) * enemyShare * WAR_DENIAL;

  return downstream + (hereAfter - hereNow) + denied;
}

/**
 * Markets this crown may post to: any it holds ground in, or one directly up- or
 * downstream of those.
 *
 * The same rule `canReachNode` states, computed once per crown per sitting
 * instead of once per candidate — `canReachNode` walks the whole province table
 * to find the crown's holdings, and calling it sixteen times inside a loop is a
 * province scan per market for an answer that cannot change while the council
 * sits.
 */
function reachableNodes(c: CampaignState, n: number): number[] {
  const held = new Set<number>();
  for (let p = 0; p < c.provinces.length; p++) {
    if (c.provinces[p].owner !== n) continue;
    const k = tradeNodeOf(c, p);
    if (k >= 0) held.add(k);
  }
  const out = new Set<number>(held);
  for (const k of held) {
    for (const to of c.trade.nodes[k].outgoing) out.add(to);
  }
  for (const node of c.trade.nodes) {
    if (node.outgoing.some((to) => held.has(to))) out.add(node.id);
  }
  return [...out].sort((a, b) => a - b);
}
